import React, { useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useSmartEntity } from 'contexts/smart-entity'
import { getUnitsSummaryTranslation } from 'utilities/organization'
import { size } from 'utilities/object'
import { refreshSmartTemplates } from 'pages/documents/utilities'
import { Fields, Field, Value, CompanyLogoWrapper, CompanyLogo } from './s'
import { Label } from 'components/form/field/s'
import Form from 'components/form/controller'
import StringField from 'components/form/field/string'
import SelectField from 'components/form/field/select'
import TextField from 'components/form/field/text'
import ShareOrEveryoneField from 'components/form/field/share-or-everyone'
import { ValueDisplay, Helper } from 'components/form/field/s'
import LanguageField from 'components/form/field/language'
import ContentLanguageSupportMessage from 'pages/documents/components/content-language-support-message'
import CountryField from 'components/form/field/country-picker'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'
import { Logo as Huma } from 'components/svg'
import schjodtSvg from 'static/gfx/partners/schjodt.svg'
import schjodtDarkSvg from 'static/gfx/partners/schjodt-dark.svg'

const PreviewSmartTemplateDetailsFields = ({
    template,
    editing, setEditing,
    categories = [],
    salt
}) => {
    const { formatMessage } = useIntl()

    const { update: updateTemplate } = useSmartEntity()

    const [shares, setShares] = useState({
        value: template?.shares ?? [],
        changed: false
    })

    const [whichShares, setWhichShares] = useState({
        value: shares.value.find(({ type }) => type === 'organization') ? 'everyone' : 'shares',
        changed: false
    })

    const [language, setLanguage] = useState(template.language ?? null)
    const [updating, setUpdating] = useState(false)

    const update = async (body, { resetTouched }) => {
        setUpdating(true)

        const { ok } = await updateTemplate({
            ...body,
            ...((body.category === 'none') ? { category: null } : null)
        })

        setUpdating(false)

        if(ok) {
            resetTouched()
            refreshSmartTemplates()
            setEditing(false)
        }
    }

    const ProvidedBy = providedToLogoMap[template.providedBy?.id] ?? null
    const ProvidedByDark = providedToDarkLogoMap[template.providedBy?.id] ?? null

    return (
        <Form
            layout="vertical"
            onSubmit={update}>
            {({ touched, errors, trigger }) => (
                <>
                    <Fields>
                        {!!editing && (
                            <>
                                <StringField
                                    salt={salt}
                                    label={formatMessage({
                                        id: 'noun_name',
                                        defaultMessage: 'Name'
                                    })}
                                    name="name"
                                    field={{
                                        value: template.name,
                                        required: true
                                    }}
                                    controlProps={{ autoFocus: true }} />
                                <SelectField
                                    salt={salt}
                                    label={formatMessage({
                                        id: 'noun_category',
                                        defaultMessage: 'Category'
                                    })}
                                    name="category"
                                    field={{
                                        value: template.category ?? 'none',
                                        include: 'touched',
                                        allowEmptyOption: false
                                    }}
                                    options={categories.map(category => ({
                                        ...category,
                                        key: category.value
                                    }))} />
                                <TextField
                                    salt={salt}
                                    label={formatMessage({
                                        id: 'noun_description',
                                        defaultMessage: 'Description'
                                    })}
                                    name="description"
                                    field={{ value: template.description }}
                                    controlProps={{
                                        rows: 4
                                    }} />
                            </>
                        )}
                        {!template.providedBy && (
                            <>
                                <ShareOrEveryoneField
                                    salt={salt}
                                    label={formatMessage({
                                        id: 'noun_access',
                                        defaultMessage: 'Access'
                                    })}
                                    name="shares"
                                    field={{
                                        value: shares.value,
                                        which: whichShares.value,
                                        editable: editing,
                                        include: shares.changed ? 'always' : 'touched'
                                    }}
                                    picker={{ types: shareTypes }}
                                    display={{
                                        empty: (
                                            <Value>
                                                <ValueDisplay>
                                                    <FormattedMessage
                                                        id="access_only_you"
                                                        defaultMessage="Only you have access" />
                                                </ValueDisplay>
                                                {!!editing && (
                                                    <Plain
                                                        onClick={() => setEditing(true)}
                                                        className="constructive">
                                                        <FormattedMessage
                                                            id="action_change"
                                                            defaultMessage="Change" />
                                                    </Plain>
                                                )}
                                            </Value>
                                        )
                                    }}
                                    onChange={({ shares, which }) => {
                                        shares = shares.map(({ type, ...rest }) => ({
                                            ...rest[type],
                                            type
                                        }))

                                        setWhichShares({
                                            value: which,
                                            changed: true
                                        })

                                        setShares({
                                            value: shares,
                                            changed: true
                                        })
                                    }}
                                    shareLabel={formatMessage(getUnitsSummaryTranslation(shareTypes))}
                                    key={`${salt}:shares:${whichShares.value}:${shares.value.map(({ id }) => id).join('+')}`} />
                                {!!editing && (
                                    <Helper>
                                        <FormattedMessage
                                            id="documents_smart_template_shares_helper"
                                            defaultMessage="People with access to a template can use it to create documents. They can’t edit the template itself, unless they have full access to the system permission “Smart document templates”." />
                                    </Helper>
                                )}
                            </>
                        )}
                        <LanguageField
                            salt={salt}
                            label={formatMessage({
                                id: 'noun_language',
                                defaultMessage: 'Language'
                            })}
                            name="language"
                            field={{
                                value: language,
                                editable: editing,
                                unsettable: false,
                                required: true
                            }}
                            onChange={({ language }) => setLanguage(language)} />
                        <ContentLanguageSupportMessage language={language} tight />
                        <CountryField
                            salt={salt}
                            label={formatMessage({
                                id: 'noun_legislation',
                                defaultMessage: 'Legislation'
                            })}
                            name="legislation"
                            field={{
                                value: template.legislation ?? null,
                                editable: editing
                            }}
                            view={{ showFlag: true }} />
                        {(ProvidedBy && ProvidedByDark) && (
                            <Field>
                                <Label>
                                    <FormattedMessage
                                        id="provided_by"
                                        defaultMessage="Provided by" />
                                </Label>
                                <ProvidedBy
                                    title={template.providedBy.name}
                                    alt={template.providedBy.name}
                                    className="light" />
                                <ProvidedByDark
                                    title={template.providedBy.name}
                                    alt={template.providedBy.name}
                                    className="dark" />
                            </Field>
                        )}
                    </Fields>
                    {!!editing && (
                        <Actions>
                            <Plain
                                onClick={() => setEditing(false)}
                                className="neutral"
                                disabled={updating}>
                                <FormattedMessage
                                    id="action_cancel"
                                    defaultMessage="Cancel" />
                            </Plain>
                            <ButtonSubmit
                                className={`constructive${updating ? ' loading' : ''}`}
                                disabled={(!touched.length && !shares.changed && !whichShares.changed) || !!size(errors) || updating}
                                ref={trigger}>
                                <FormattedMessage
                                    id="action_save"
                                    defaultMessage="Save" />
                            </ButtonSubmit>
                        </Actions>
                    )}
                </>
            )}
        </Form>
    )
}

const shareTypes = ['user', 'team', 'location']

const providedToLogoMap = {
    huma: props => (
        <CompanyLogoWrapper>
            <Huma {...props} width={83} height={24} />
        </CompanyLogoWrapper>
    ),
    schjodt: props => <CompanyLogo {...props} src={schjodtSvg} $size={24} />
}

const providedToDarkLogoMap = {
    huma: props => (
        <CompanyLogoWrapper>
            <Huma {...props} width={83} height={24} />
        </CompanyLogoWrapper>
    ),
    schjodt: props => <CompanyLogo {...props} src={schjodtDarkSvg} $size={24} />
}

export default PreviewSmartTemplateDetailsFields